/**
 * Página "Jornadas" para jugadores
 * 
 * Muestra:
 * - Jornadas próximas
 * - Historial de jornadas
 * - Evaluaciones y resultados
 */

import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../../services/api';
import { API_URL } from '../../config';

const PlayerJornadas = () => {
  const { playerData } = useOutletContext();
  const [jornadasData, setJornadasData] = useState(null);
  const [historialJornadas, setHistorialJornadas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingHistorial, setLoadingHistorial] = useState(true);
  const [error, setError] = useState(null);
  const [procesandoInscripcion, setProcesandoInscripcion] = useState(null);
  const [procesandoAviso, setProcesandoAviso] = useState(false);
  const [jornadaActualIndex, setJornadaActualIndex] = useState(0);

  // Función para cargar jornadas
  const cargarJornadas = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔑 Cargando jornadas próximas...');

      const response = await API.get('/player/jornadas');

      console.log('📡 Respuesta del servidor:', response.data);

      if (response.data.success) {
        console.log('✅ Datos de jornadas cargados:', response.data.data);
        setJornadasData(response.data.data);
        // Resetear el índice si no hay jornadas o si el índice actual es mayor que las jornadas disponibles
        if (!response.data.data.jornadas || response.data.data.jornadas.length === 0) {
          setJornadaActualIndex(0);
        } else if (jornadaActualIndex >= response.data.data.jornadas.length) {
          setJornadaActualIndex(0);
        }
      } else {
        console.log('❌ Error en respuesta:', response.data);
        setError('Error al cargar las jornadas');
      }
    } catch (err) {
      console.error('❌ Error al cargar jornadas:', err);
      console.error('❌ Detalles del error:', err.response?.data);
      setError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  // Función para inscribirse en una jornada
  const inscribirseEnJornada = async (jornadaId) => {
    try {
      setProcesandoInscripcion(jornadaId);

      const response = await API.post(`/player/jornadas/${jornadaId}/inscribir`);

      if (response.data.success) {
        console.log('✅ Inscripción exitosa:', response.data);
        // Recargar jornadas para actualizar el estado
        await cargarJornadas();
      } else {
        setError(response.data.message || 'Error al inscribirse');
      }
    } catch (err) {
      console.error('❌ Error al inscribirse:', err);
      setError(err.response?.data?.message || 'Error al inscribirse en la jornada');
    } finally {
      setProcesandoInscripcion(null);
    }
  };

  // Función para desinscribirse de una jornada
  const desinscribirseDeJornada = async (jornadaId) => {
    try {
      setProcesandoInscripcion(jornadaId);

      const response = await API.delete(`/player/jornadas/${jornadaId}/desinscribir`);

      if (response.data.success) {
        console.log('✅ Desinscripción exitosa:', response.data);
        // Recargar jornadas para actualizar el estado
        await cargarJornadas();
      } else {
        setError(response.data.message || 'Error al desinscribirse');
      }
    } catch (err) {
      console.error('❌ Error al desinscribirse:', err);
      setError(err.response?.data?.message || 'Error al desinscribirse de la jornada');
    } finally {
      setProcesandoInscripcion(null);
    }
  };

  // Funciones de navegación
  const irJornadaAnterior = () => {
    if (jornadaActualIndex > 0) {
      setJornadaActualIndex(jornadaActualIndex - 1);
    }
  };

  const irJornadaSiguiente = () => {
    if (jornadasData && jornadaActualIndex < jornadasData.jornadas.length - 1) {
      setJornadaActualIndex(jornadaActualIndex + 1);
    }
  };

  // Función para avisar al agente
  const avisarAgente = async () => {
    try {
      setProcesandoAviso(true);

      const response = await API.post('/player/avisar-agente', {
        motivo: 'sin_jornadas',
        mensaje: 'El futbolista no tiene jornadas programadas y solicita asistencia'
      });

      if (response.data.success) {
        // Mostrar mensaje de éxito
        alert('✅ Aviso enviado correctamente a tu agente. Recibirás una respuesta pronto.');
      } else {
        alert('❌ Error al enviar el aviso. Inténtalo de nuevo.');
      }
    } catch (err) {
      console.error('❌ Error al avisar al agente:', err);
      alert('❌ Error al conectar con el servidor. Inténtalo de nuevo.');
    } finally {
      setProcesandoAviso(false);
    }
  };

  // Función para determinar si el botón "No Voy" debe estar disponible
  const puedeDesinscribirse = (jornada, index) => {
    // Solo disponible para la jornada más cercana (índice 0)
    if (index !== 0) return false;

    // Debe estar inscrito
    if (!jornada.inscrito) return false;

    // El plazo de inscripción no debe haber expirado
    if (jornada.fecha_limite_inscripcion) {
      const ahora = new Date();
      const fechaLimite = new Date(jornada.fecha_limite_inscripcion);
      if (ahora > fechaLimite) return false;
    }

    return true;
  };

  // Función para cargar historial de jornadas
  const cargarHistorialJornadas = async () => {
    try {
      setLoadingHistorial(true);
      const response = await API.get('/player/historial-jornadas');

      if (response.data.success) {
        setHistorialJornadas(response.data.data);
      } else {
        console.error('Error al cargar el historial de jornadas:', response.data);
        // No establecer error general, solo para el historial
        setHistorialJornadas({ jornadas_historial: [], total_jornadas_disputadas: 0 });
      }
    } catch (err) {
      console.error('❌ Error al cargar historial de jornadas:', err);
      // No establecer error general, solo para el historial
      setHistorialJornadas({ jornadas_historial: [], total_jornadas_disputadas: 0 });
    } finally {
      setLoadingHistorial(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    console.log('API_URL:', API_URL); // Debug
    cargarJornadas(); // Cargar jornadas al montar el componente
    cargarHistorialJornadas(); // Cargar historial de jornadas
  }, []);

  const containerStyle = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: window.innerWidth <= 768 ? '20px' : '30px',
    marginBottom: '25px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #E8ECFF'
  };

  const titleStyle = {
    fontSize: window.innerWidth <= 768 ? '1.8rem' : '2.2rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '30px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '15px'
  };

  const sectionTitleStyle = {
    fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.6rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  if (!playerData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Cargando jornadas...</p>
      </div>
    );
  }

  const isMobile = window.innerWidth <= 768;

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>
        ⚽ Mis Jornadas
      </h1>

      {/* Próximas Jornadas */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={sectionTitleStyle}>📅 Próximas Jornadas</h2>

          {/* Mostrar saldo de jornadas */}
          {jornadasData && (
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#e8f5e8',
              borderRadius: '6px',
              fontSize: '0.9rem',
              color: '#2d5a2d',
              fontWeight: '600'
            }}>
              💰 Saldo: {jornadasData.saldo_jornadas} jornadas
            </div>
          )}
        </div>

        {loading ? (
          <div style={{
            backgroundColor: '#F8F9FF',
            padding: '40px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #E8ECFF'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '15px' }}>⏳</div>
            <p style={{ margin: 0, color: '#666' }}>Cargando jornadas...</p>
          </div>
        ) : error ? (
          <div style={{
            backgroundColor: '#FFEBEE',
            padding: '40px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #FFCDD2'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '15px' }}>❌</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#d32f2f' }}>Error al cargar</h3>
            <p style={{ margin: 0, color: '#666', textAlign: 'center' }}>{error}</p>
          </div>
        ) : jornadasData?.jornadas?.length > 0 ? (
          <div style={{ marginBottom: '40px' }}>
            {/* Navegación de jornadas */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#F8F9FF',
              borderRadius: '10px',
              border: '1px solid #E8ECFF'
            }}>
              <button
                onClick={irJornadaAnterior}
                disabled={jornadaActualIndex === 0}
                style={{
                  padding: '10px 15px',
                  borderRadius: '8px',
                  border: '1px solid #4CAF50',
                  backgroundColor: jornadaActualIndex === 0 ? '#ccc' : '#4CAF50',
                  color: 'white',
                  fontSize: '0.9rem',
                  cursor: jornadaActualIndex === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                ← Anterior
              </button>

              <div style={{
                textAlign: 'center',
                color: '#061156',
                fontWeight: '600'
              }}>
                Jornada {jornadaActualIndex + 1} de {jornadasData.jornadas.length}
              </div>

              <button
                onClick={irJornadaSiguiente}
                disabled={jornadaActualIndex >= jornadasData.jornadas.length - 1}
                style={{
                  padding: '10px 15px',
                  borderRadius: '8px',
                  border: '1px solid #4CAF50',
                  backgroundColor: jornadaActualIndex >= jornadasData.jornadas.length - 1 ? '#ccc' : '#4CAF50',
                  color: 'white',
                  fontSize: '0.9rem',
                  cursor: jornadaActualIndex >= jornadasData.jornadas.length - 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Siguiente →
              </button>
            </div>

            {/* Mostrar solo la jornada actual */}
            {(() => {
              const jornada = jornadasData.jornadas[jornadaActualIndex];
              const index = jornadaActualIndex;
              return (
              <div key={index} style={{
                backgroundColor: '#F8F9FF',
                padding: '20px',
                borderRadius: '12px',
                border: `2px solid ${jornada.inscrito ? '#4CAF50' : '#E8ECFF'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#061156', fontSize: '1.2rem' }}>
                      Jornada {jornada.numero} - {jornada.torneo.nombre}
                    </h3>
                    <p style={{ margin: '0 0 5px 0', color: '#666', fontSize: '0.95rem' }}>
                      📍 {jornada.sede.nombre}
                    </p>
                    <p style={{ margin: '0', color: '#888', fontSize: '0.85rem' }}>
                      {jornada.sede.direccion}, {jornada.sede.poblacion}
                    </p>
                  </div>
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    backgroundColor: jornada.inscrito ? '#4CAF50' : '#FF9800',
                    color: 'white'
                  }}>
                    {jornada.inscrito ? '✅ Inscrito' : '⏳ No inscrito'}
                  </span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                  gap: '15px',
                  padding: '15px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  fontSize: '0.9rem'
                }}>
                  <div>
                    <strong>📅 Fecha:</strong><br />
                    {new Date(jornada.fecha).toLocaleDateString('es-ES', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                  <div>
                    <strong>⏰ Hora:</strong><br />
                    {jornada.hora}
                  </div>
                  <div>
                    <strong>💰 Saldo:</strong><br />
                    {jornadasData.saldo_jornadas} jornadas
                  </div>
                </div>

                {/* Estado de inscripción y botones */}
                <div style={{ marginTop: '15px' }}>
                  {jornada.inscrito ? (
                    <div>
                      <div style={{
                        padding: '10px 15px',
                        backgroundColor: '#E8F5E8',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        color: '#2d5a2d',
                        border: '1px solid #4CAF50',
                        marginBottom: '10px'
                      }}>
                        ✅ <strong>Estás inscrito en esta jornada</strong>
                      </div>

                      {puedeDesinscribirse(jornada, index) ? (
                        <button
                          onClick={() => desinscribirseDeJornada(jornada.id)}
                          disabled={procesandoInscripcion === jornada.id}
                          style={{
                            padding: '10px 20px',
                            borderRadius: '6px',
                            border: '1px solid #f44336',
                            backgroundColor: procesandoInscripcion === jornada.id ? '#ccc' : '#f44336',
                            color: 'white',
                            fontSize: '0.9rem',
                            cursor: procesandoInscripcion === jornada.id ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {procesandoInscripcion === jornada.id ? '⏳ Procesando...' : '❌ No voy'}
                        </button>
                      ) : jornada.inscrito && index !== 0 ? (
                        <div style={{
                          padding: '10px 15px',
                          backgroundColor: '#FFF3E0',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          color: '#E65100',
                          border: '1px solid #FFB74D'
                        }}>
                          ℹ️ Solo puedes cancelar la jornada más próxima
                        </div>
                      ) : jornada.inscrito && jornada.fecha_limite_inscripcion && new Date() > new Date(jornada.fecha_limite_inscripcion) ? (
                        <div style={{
                          padding: '10px 15px',
                          backgroundColor: '#FFEBEE',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          color: '#d32f2f',
                          border: '1px solid #FFCDD2'
                        }}>
                          ⏰ Plazo de cancelación expirado
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div>
                      {jornada.puede_inscribirse ? (
                        <div>
                          <div style={{
                            padding: '10px 15px',
                            backgroundColor: '#FFF3E0',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            color: '#E65100',
                            border: '1px solid #FFB74D',
                            marginBottom: '10px'
                          }}>
                            ⚠️ <strong>Límite inscripción:</strong> {new Date(jornada.fecha_limite_inscripcion).toLocaleString('es-ES')}
                          </div>

                          <button
                            onClick={() => inscribirseEnJornada(jornada.id)}
                            disabled={procesandoInscripcion === jornada.id}
                            style={{
                              padding: '10px 20px',
                              borderRadius: '6px',
                              border: '1px solid #4CAF50',
                              backgroundColor: procesandoInscripcion === jornada.id ? '#ccc' : '#4CAF50',
                              color: 'white',
                              fontSize: '0.9rem',
                              cursor: procesandoInscripcion === jornada.id ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {procesandoInscripcion === jornada.id ? '⏳ Procesando...' : '✅ Sí voy'}
                          </button>
                        </div>
                      ) : (
                        <div style={{
                          padding: '10px 15px',
                          backgroundColor: '#FFEBEE',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          color: '#d32f2f',
                          border: '1px solid #FFCDD2'
                        }}>
                          {jornadasData.saldo_jornadas <= 0 ?
                            '💰 Sin saldo suficiente para inscribirse' :
                            '⏰ Plazo de inscripción expirado'
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              );
            })()}
          </div>
        ) : (
          <div style={{
            backgroundColor: '#FFF3E0',
            padding: '40px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #FFB74D'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📅</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#E65100' }}>No tienes jornadas</h3>
            <p style={{ margin: '0 0 20px 0', color: '#666', textAlign: 'center' }}>
              No tienes jornadas programadas actualmente. Avisa a tu agente.
            </p>
            <button
              onClick={avisarAgente}
              disabled={procesandoAviso}
              style={{
                padding: '12px 24px',
                backgroundColor: procesandoAviso ? '#ccc' : '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: procesandoAviso ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
            >
              {procesandoAviso ? (
                <>
                  <span>⏳</span>
                  Enviando aviso...
                </>
              ) : (
                <>
                  <span>🤝</span>
                  Avisar a mi agente
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Historial de Jornadas */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          📊 Historial de Jornadas
        </h2>

        {loadingHistorial ? (
          <div style={{
            backgroundColor: '#F8F9FF',
            padding: '40px',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #E8ECFF'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '15px' }}>⏳</div>
            <p style={{ margin: 0, color: '#666' }}>Cargando historial de jornadas...</p>
          </div>
        ) : historialJornadas && historialJornadas.jornadas_historial && historialJornadas.jornadas_historial.length > 0 ? (
          <div>
            <div style={{
              backgroundColor: '#E8F5E8',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #4CAF50'
            }}>
              <p style={{
                margin: 0,
                color: '#2E7D32',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                📈 Total de jornadas disputadas: {historialJornadas.total_jornadas_disputadas}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '20px'
            }}>
              {historialJornadas.jornadas_historial.map((jornada) => (
              <div key={jornada.id} style={{
                backgroundColor: '#F8F9FF',
                padding: '22px',
                borderRadius: '15px',
                border: '2px solid #E8ECFF'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <h4 style={{
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    color: '#061156',
                    margin: 0
                  }}>
                    Jornada {jornada.numero}
                  </h4>
                  <span style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '15px',
                    fontSize: '0.85rem',
                    fontWeight: '700'
                  }}>
                    +{jornada.propoints_ganados} PP
                  </span>
                </div>
                
                <div style={{
                  fontSize: '1rem',
                  color: '#666',
                  marginBottom: '15px',
                  fontWeight: '500'
                }}>
                  📅 {new Date(jornada.fecha).toLocaleDateString('es-ES', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })} • ⏰ {jornada.hora || 'No especificada'}
                </div>

                <div style={{
                  fontSize: '0.9rem',
                  color: '#888',
                  marginBottom: '15px'
                }}>
                  📍 {jornada.sede.nombre} - {jornada.sede.poblacion}
                </div>
                
                {jornada.evaluacion && (
                  <div style={{
                    backgroundColor: 'white',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #E8ECFF'
                  }}>
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#4352A9',
                      marginBottom: '10px'
                    }}>
                      Evaluación:
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px',
                      fontSize: '0.85rem'
                    }}>
                      <div>🎯 Técnica: <strong>{jornada.evaluacion.tecnica}/10</strong></div>
                      <div>🧠 Táctica: <strong>{jornada.evaluacion.tactica}/10</strong></div>
                      <div>💪 Física: <strong>{jornada.evaluacion.fisica}/10</strong></div>
                      <div>🧘 Mental: <strong>{jornada.evaluacion.mental}/10</strong></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#F5F5F5',
            padding: '30px',
            borderRadius: '15px',
            textAlign: 'center',
            border: '2px dashed #CCCCCC'
          }}>
            <p style={{
              fontSize: '1.1rem',
              color: '#666',
              margin: 0,
              fontWeight: '500'
            }}>
              📋 Aún no has participado en ninguna jornada disputada
            </p>
            <p style={{
              fontSize: '0.9rem',
              color: '#888',
              margin: '10px 0 0 0'
            }}>
              Las jornadas aparecerán aquí después de ser disputadas
            </p>
          </div>
        )}
      </div>

      {/* Información Adicional */}
      <div style={cardStyle}>
        <div style={{
          backgroundColor: '#E3F2FD',
          padding: '20px',
          borderRadius: '10px',
          border: '2px solid #2196F3',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '1rem',
            color: '#1565C0',
            margin: 0,
            fontWeight: '500'
          }}>
            💡 Las jornadas se programan automáticamente según tu inscripción. 
            Recibirás notificaciones sobre próximas jornadas.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlayerJornadas;
